import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import toast from 'react-hot-toast';
import { Turnstile } from '@marsidev/react-turnstile';
import { GlassCard, GlassButton, GlassInput } from '../../components/ui/GlassComponents';
import SSOLoginButton from '../../components/auth/SSOLoginButton';

const TURNSTILE_SITE_KEY = '0x4AAAAAADAvVFOq2COJbA6Q';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [ssoAvailable, setSsoAvailable] = useState(false);
  const [checkingSso, setCheckingSso] = useState(false);
  const [userTeamId, setUserTeamId] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);
  const { login } = useAuth();
  const { darkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Carousel images
  const backgroundImages = [
    '/images/loginimg1.png',
    '/images/loginimg2.png'
  ];
  
  /// Get redirect path from location state or default to dashboard
  const from = location.state?.from?.pathname || '/dashboard';
  
  // Auto-rotate carousel images every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex + 1) % backgroundImages.length
      );
    }, 5000); // Change image every 5 seconds
    
    return () => clearInterval(interval);
  }, [backgroundImages.length]);

  // Handle SSO token from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const ssoToken = urlParams.get('sso_token');
    const ssoSuccess = urlParams.get('sso_success');
    const ssoError = urlParams.get('sso_error');

    if (ssoError) {
      toast.error(`SSO Login Failed: ${ssoError}`);
      // Clean up URL parameters
      navigate('/login', { replace: true });
      return;
    }

    if (ssoToken && ssoSuccess === 'true') {
      setLoading(true);
      
      // Store the token and redirect to dashboard
      localStorage.setItem('token', ssoToken);
      
      // Verify the token and get user info
      fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${ssoToken}`
        }
      })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          toast.success('Successfully logged in with SSO!');
          // Update auth context
          login(data.user, ssoToken);
          navigate(from, { replace: true });
        } else {
          throw new Error('Invalid SSO token');
        }
      })
      .catch(error => {
        console.error('SSO token verification failed:', error);
        toast.error('SSO login verification failed');
        localStorage.removeItem('token');
        navigate('/login', { replace: true });
      })
      .finally(() => {
        setLoading(false);
      });
    }
  }, [location.search, navigate, from, login]);

  // Check if SSO is available for the user's email domain
  const checkSSOAvailability = async (emailValue) => {
    if (!emailValue || !emailValue.includes('@')) {
      setSsoAvailable(false);
      setUserTeamId(null);
      return;
    }

    setCheckingSso(true);
    try {
      // Check if user exists and their team has SSO enabled
      const response = await fetch(`/api/auth/check-sso?email=${encodeURIComponent(emailValue)}`);
      const data = await response.json();
      
      if (data.success && data.ssoEnabled) {
        setSsoAvailable(true);
        setUserTeamId(data.teamId);
      } else {
        setSsoAvailable(false);
        setUserTeamId(null);
      }
    } catch (error) {
      console.error('Error checking SSO availability:', error);
      setSsoAvailable(false);
      setUserTeamId(null);
    } finally {
      setCheckingSso(false);
    }
  };

  // Debounce timer ref
  const ssoCheckTimeoutRef = React.useRef(null);

  // Handle email change with proper debouncing
  const handleEmailChange = (e) => {
    const emailValue = e.target.value;
    setEmail(emailValue);
    
    // Clear existing timeout
    if (ssoCheckTimeoutRef.current) {
      clearTimeout(ssoCheckTimeoutRef.current);
    }
    
    // Only check SSO if email looks complete (has @ and domain)
    if (emailValue.includes('@') && emailValue.includes('.')) {
      // Debounce SSO check - only check after user stops typing
      ssoCheckTimeoutRef.current = setTimeout(() => {
        checkSSOAvailability(emailValue);
      }, 800); // Increased delay to reduce API calls
    } else {
      // Reset SSO state for incomplete emails
      setSsoAvailable(false);
      setUserTeamId(null);
    }
  };

  // Handle email blur - check immediately when user leaves field
  const handleEmailBlur = () => {
    if (email.includes('@') && email.includes('.')) {
      // Clear any pending timeout and check immediately
      if (ssoCheckTimeoutRef.current) {
        clearTimeout(ssoCheckTimeoutRef.current);
      }
      checkSSOAvailability(email);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (ssoCheckTimeoutRef.current) {
        clearTimeout(ssoCheckTimeoutRef.current);
      }
    };
  }, []);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please enter both email and password');
      return;
    }
    
    if (!turnstileToken) {
      toast.error('Security check not ready — please wait a moment and try again');
      return;
    }

    setLoading(true);

    try {
      const result = await login(email, password, turnstileToken);

      if (result.success) {
        toast.success('Login successful!');
        navigate(from, { replace: true });
      } else {
        toast.error(result.message || 'Login failed. Please check your credentials.');
        // A Turnstile token is single-use — get a fresh one for the retry.
        turnstileRef.current?.reset();
        setTurnstileToken(null);
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('An error occurred during login. Please try again.');
      turnstileRef.current?.reset();
      setTurnstileToken(null);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-slate-900 dark:to-gray-800">
      <div className="min-h-screen flex">
        {/* Left Side - Branding */}
        <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 relative overflow-hidden">
          {/* Background Image Carousel */}
          {backgroundImages.map((image, index) => (
            <div 
              key={index}
              className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ease-in-out ${
                index === currentImageIndex ? 'opacity-30' : 'opacity-0'
              }`}
              style={{
                backgroundImage: `url(${image})`
              }}
            ></div>
          ))}
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16">
            <div className="mb-8">
              <img 
                src="/images/lwlogo.png" 
                alt="LeaveWise" 
                className="h-20 w-auto mb-6"
              />
              <h1 className="text-4xl xl:text-5xl font-bold text-white mb-4">
                Welcome to LeaveWise
              </h1>
              <p className="text-xl text-blue-100 mb-8">
                Streamline your team's leave management with our modern, intuitive platform.
              </p>
              <div className="space-y-4 text-blue-100">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-300 rounded-full"></div>
                  <span>Easy leave requests and approvals</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-300 rounded-full"></div>
                  <span>Real-time team calendar</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-300 rounded-full"></div>
                  <span>Comprehensive analytics</span>
                </div>
              </div>
            </div>
          </div>
          {/* Decorative elements */}
          <div className="absolute top-20 right-20 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
          <div className="absolute bottom-20 left-20 w-24 h-24 bg-white/10 rounded-full blur-xl"></div>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            {/* Mobile Logo (only visible on small screens) */}
            <div className="lg:hidden text-center mb-8">
              <div className="flex justify-center mb-6">
                <img 
                  src="/images/lwlogo.png" 
                  alt="LeaveWise" 
                  className="h-16 w-auto"
                />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Welcome to LeaveWise
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Sign in to continue to your dashboard
              </p>
            </div>

            {/* Desktop Header */}
            <div className="hidden lg:block text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Sign In
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Access your dashboard
              </p>
            </div>

            {/* Login Form */}
            <GlassCard className="p-8">
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="email-address" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email address
                  </label>
                  <GlassInput
                    id="email-address"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    placeholder="Enter your email"
                    value={email}
                    onChange={handleEmailChange}
                    onBlur={handleEmailBlur}
                    disabled={loading}
                  />
                </div>
                {/* Only show password field if SSO is not available or still checking */}
                {!ssoAvailable && (
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Password
                    </label>
                    <GlassInput
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      required={!ssoAvailable}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                )}
              </div>

              {/* Only show password-related options when password field is visible */}
              {!ssoAvailable && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      id="remember-me"
                      name="remember-me"
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                      Remember me
                    </label>
                  </div>

                  <div className="text-sm">
                    <Link 
                      to="/forgot-password" 
                      className="font-medium text-blue-600 hover:text-blue-500 transition-colors"
                    >
                      Forgot password?
                    </Link>
                  </div>
                </div>
              )}

              {/* Cloudflare Turnstile — only mounted on the password path.
                  The SSO button below triggers a redirect straight to Azure AD
                  and doesn't need a captcha. */}
              {!ssoAvailable && (
                <>
                  <div className="flex justify-center">
                    <Turnstile
                      ref={turnstileRef}
                      siteKey={TURNSTILE_SITE_KEY}
                      options={{ theme: darkMode ? 'dark' : 'light', appearance: 'interaction-only' }}
                      onSuccess={setTurnstileToken}
                      onExpire={() => setTurnstileToken(null)}
                      onError={() => setTurnstileToken(null)}
                    />
                  </div>
                  <GlassButton
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="w-full"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Signing in...
                      </>
                    ) : (
                      'Sign in'
                    )}
                  </GlassButton>
                </>
              )}

              {/* SSO Login Section - Primary login method when available */}
              {ssoAvailable && (
                <div className="space-y-4">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Your organisation uses Single Sign-On (SSO)
                    </p>
                  </div>
                  
                  <SSOLoginButton 
                    teamId={userTeamId}
                    onLoading={setLoading}
                  />
                </div>
              )}

              {checkingSso && (
                <div className="mt-4 text-center">
                  <div className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Checking SSO availability...
                  </div>
                </div>
              )}
            </form>
        </GlassCard>
        
        {/* Sign up link */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Don't have an account?{' '}
            <Link 
              to="/signup" 
              className="font-medium text-blue-600 hover:text-blue-500 transition-colors"
            >
              Create your team
            </Link>
          </p>
        </div>

        {/* Dark Mode Toggle */}
        <div className="flex justify-center mt-4">
          <GlassButton
            onClick={toggleTheme}
            variant="secondary"
            size="sm"
            className="!p-2 !rounded-full"
            aria-label="Toggle dark mode"
          >
            {darkMode ? (
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
              </svg>
            )}
          </GlassButton>
        </div>

        {/* Copyright */}
        <div className="text-center mt-8">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            &copy; {new Date().getFullYear()} LeaveWise. All rights reserved.
          </p>
        </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
